/**
 * Progress, computed client-side so the bar moves as the annotator works rather than only
 * after a save round-trip. It mirrors `hismaic/api/views.py::image_progress` exactly, and the
 * server's number replaces it after every successful save — if the two ever disagree, the
 * server's is right and the discrepancy is worth a bug report.
 *
 * The semantics that matter (and the reason this is not just `boxes.length / tokens.length`):
 * an expected token is **resolved** when it is boxed *or* explicitly marked not-present. An
 * annotator who has honestly recorded "OCIANA reads a `b` here and the stone does not have
 * one" is finished with that token. A progress bar that still counts it as outstanding is
 * pressuring them to invent a box, which is exactly the failure mode this project cannot
 * afford.
 */

import type { ImageProgress } from './api/types';
import type { DraftBox } from './draft';

export function imageProgress(
  expectedLetters: number,
  boxes: readonly DraftBox[],
  absent: ReadonlySet<number>,
): ImageProgress {
  let boxedExpected = 0;
  let unexpected = 0;
  for (const box of boxes) {
    if (box.alignment === 'expected') boxedExpected += 1;
    else unexpected += 1;
  }
  const absentCount = absent.size;
  const resolved = expectedLetters
    ? Math.min(boxedExpected + absentCount, expectedLetters)
    : boxedExpected + absentCount;
  const remaining = Math.max(expectedLetters - resolved, 0);
  return {
    expected_letters: expectedLetters,
    boxed_expected: boxedExpected,
    absent: absentCount,
    unexpected,
    resolved,
    remaining,
    complete: expectedLetters > 0 && remaining === 0,
  };
}

export function percent(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(100, Math.max(0, (part / whole) * 100));
}
